import { useEffect, useState } from "react";
import { Button } from "@headlessui/react";
import { handleEnterKey } from "../../app/keyboardUtils";
import Select from "../../components/input/Select";
import { SimpleOption } from "../../components/input/SimpleListbox";
import { useAddUserToDistrictMutation, useGetDistrictsQuery } from "./districtsApiSlice";

interface Props {
	userId: number;
	districtIds: number[];
	onClose: () => void;
	onSwitchToNewTab?: () => void;
}

export default function AddExistingDistrictToUser(props: Props) {
	const [selectedDistrict, setSelectedDistrict] = useState<number>();
	const [districtOptions, setDistrictOptions] = useState<SimpleOption[]>([]);

	const getDistrictsResult = useGetDistrictsQuery();
	const districts = getDistrictsResult.data;

	const [addToDistrict] = useAddUserToDistrictMutation();

	useEffect(() => {
		if (districts) {
			const options = districts
				.filter((d) => !props.districtIds.includes(d.id))
				.map((d) => ({ value: d.id, label: d.name }) as SimpleOption);

			setDistrictOptions(options);

			// Handle tab switching and default selection in the same effect
			if (options.length > 0) {
				setSelectedDistrict(Number(options[0].value));
			} else if (options.length === 0 && props.onSwitchToNewTab) {
				props.onSwitchToNewTab();
			}
		}
	}, [districts, props.districtIds, props.onSwitchToNewTab]);

	function handleAddDistrict() {
		if (selectedDistrict) {
			addToDistrict({ districtId: selectedDistrict, userId: props.userId })
				.unwrap()
				.then(() => {
					props.onClose();
				});
		}
	}

	function handleCancel() {
		props.onClose();
	}

	return (
		<section>
			{districts && districtOptions && districtOptions.length > 0 && selectedDistrict ? (
				<Select
					value={selectedDistrict}
					setValue={(v) => setSelectedDistrict(v as number)}
					simpleListBoxId="districtDropdown"
					options={districtOptions}
					label="District:"
					fieldClasses="pt-3"
					simpleListBoxClasses="w-full"
				/>
			) : (
				<div className="mt-4 px-4 py-4 bg-gray-50 border border-gray-200 rounded-lg">
					<p className="text-gray-700 text-center">
						There are no districts available to add for the user.
					</p>
				</div>
			)}
			<div className="mt-6 grid grid-cols-2 gap-3">
				<Button
					id="cancel-button"
					onClick={handleCancel}
					onKeyDown={(e) => handleEnterKey(e, handleCancel)}
					className="btn btn-neutral">
					Cancel
				</Button>
				<Button
					id="confirm-button"
					disabled={!selectedDistrict}
					onClick={handleAddDistrict}
					onKeyDown={(e) => handleEnterKey(e, handleAddDistrict)}
					className="btn btn-primary">
					Add District
				</Button>
			</div>
		</section>
	);
}
